import type { HrApplicationDetail, HrScreening } from '../../lib/jobsApi';

/*
 * The screening shape a submission takes the instant a re-screen is accepted. The server has
 * already reset the row; this is the optimistic mirror of that reset so the card does not keep
 * showing a stale score for one poll interval.
 *
 * `attemptCount` is deliberately ABSENT from this literal, not zeroed. hrReview.ts's rescreen
 * route resets status/tries/currentAttemptId/startedAt/finishedAt but leaves attemptCount alone
 * — its own comment says so ("attempt_count is deliberately NOT reset: the next attempt must get
 * a fresh attempt_no"), because it counts attempts forever, not attempts-since-last-rescreen. A
 * mirror that zeroed it would show "0 attempts" on a submission that had just been re-screened,
 * which is exactly the recruiter-facing confusion that comment exists to avoid — reintroducing it
 * here, one file over, would be the same mistake with a different author. Every field below IS
 * genuinely reset (currentAttemptId -> null clears every attempt-sourced field, and the head's
 * own finishedAt is nulled directly), which is why they can be zeroed/nulled with confidence.
 * markScreeningQueued's `{ ...s.screening, ...QUEUED_SCREENING }` spread is what makes omitting a
 * key here mean "keep whatever the last real fetch had," not "undefined."
 */
export const QUEUED_SCREENING: Omit<HrScreening, 'screenshotUrls' | 'attemptCount'> & { screenshotUrls: null } = {
  status: 'queued',
  attemptNo: null,
  outcome: null,
  lane: null,
  scoringMethod: null,
  promptVersion: null,
  model: null,
  score: null,
  summary: null,
  linkAlive: null,
  error: null,
  finishedAt: null,
  report: null,
  screenshotUrls: null,
};

/*
 * The optimistic flip lives on the PARENT, which owns the fetched detail — never inside
 * SubmissionCard. A card that copies `submission.screening` into its own useState is seeded once
 * and then permanently shadows the prop (the parent re-renders rather than remounts, because the
 * key is a stable submission id), so every later refetch renders the stale value. Keep the state
 * where the data is.
 */
export function markScreeningQueued<T extends { submissions: HrApplicationDetail['submissions'] }>(
  detail: T,
  submissionId: string,
): T {
  return {
    ...detail,
    submissions: detail.submissions.map((s) =>
      s.id === submissionId && s.screening ? { ...s, screening: { ...s.screening, ...QUEUED_SCREENING } } : s,
    ),
  };
}
